import { FC } from 'react'

import clsx from 'clsx'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

import { ConversionButton, Txt } from 'src/components/atoms'

import { useAuth } from 'src/hooks'
import { Routes } from 'src/routes'

function SamlSignInButton({ onClick }: { onClick: () => void }) {
  const text = '企業アカウントでログイン'
  const iconPath = 'log-in/soft/line'

  return (
    <>
      <div className="tablet:tw-hidden">
        <ConversionButton size="m" prependIconPath={iconPath} text={text} onClick={onClick} />
      </div>
      <div className="tw-hidden tablet:tw-block">
        <ConversionButton size="l" prependIconPath={iconPath} text={text} onClick={onClick} />
      </div>
    </>
  )
}

type Props = {
  idpAuthURL: string
  isOnlySamlEnabled: boolean
  isSuperAdmin?: boolean
  className?: string
}

export const SamlSignIn: FC<Props> = ({
  idpAuthURL,
  isOnlySamlEnabled,
  isSuperAdmin,
  className,
}) => {
  const { removeAllCookie } = useAuth()
  const searchParams = useSearchParams()

  const handleOnClick = async () => {
    // ブラウザキャッシュにより signin に戻されてしまう事があるため、キャッシュが効かないようにする
    // 参考:https://chat.openai.com/share/4d9be4c4-c35c-42c9-a803-fe9749642c9d
    const nocache = `nocache=${Date.now()}`
    const baseUrl = `${idpAuthURL}&${nocache}`

    const ssoFrom = searchParams.get('sso_from') ?? undefined
    const to = searchParams.get('to') ?? undefined
    const projectId = searchParams.get('project_id') ?? undefined
    // 予約管理経由の場合、SAML認証からのリダイレクト後に予約管理側へ戻すためにパラメータを付与する
    const isViaReservation = searchParams.get('via_reservation') === 'true'
    const viaReservationQuery = isViaReservation ? '&via_reservation=true' : ''

    await removeAllCookie()

    if (ssoFrom) {
      const ssoParameters = `&sso_from=${ssoFrom}`
      let parameters: string
      if (to) {
        parameters = `${ssoParameters}&to=${decodeURIComponent(to)}${viaReservationQuery}`
      } else if (projectId) {
        parameters = `${ssoParameters}&project_id=${projectId}${viaReservationQuery}`
      } else {
        parameters = `${ssoParameters}${viaReservationQuery}`
      }

      // stateはbase64エンコードする必要がある
      // 参考: https://docs.aws.amazon.com/ja_jp/cognito/latest/developerguide/authorization-endpoint.html
      window.location.href = `${baseUrl}&state=${btoa(encodeURIComponent(parameters))}`
      return
    }

    window.location.href = baseUrl
  }

  return (
    <section className={className}>
      <div
        className={clsx(
          isOnlySamlEnabled && 'tw-bg-[rgba(255,255,255,0.6)] tw-rounded-[1.14rem] tw-p-r3'
        )}
      >
        <div className="tw-flex tw-flex-col tw-items-center">
          {isOnlySamlEnabled && (
            <Txt className="tw-font-bold tw-text-roovPrimary-default tw-text-center tw-mb-s1">
              ログイン
            </Txt>
          )}
          <SamlSignInButton onClick={handleOnClick} />
          {isOnlySamlEnabled && isSuperAdmin && (
            <Txt sizeType="s" className="tw-font-bold tw-mt-s1">
              全権管理者の方は、メールアドレスとパスワードを使用して
              <Link
                href={Routes.IdPasswordSignIn.path}
                className="tw-underline hover:tw-no-underline"
              >
                ログイン
              </Link>
              して下さい
            </Txt>
          )}
        </div>
      </div>
    </section>
  )
}
